import type {
  CreateInvitationRequest,
  Invitation,
  InvitationFilters,
  InvitationForPublic,
  InvitationForUser,
  UpdateInvitationRequest,
} from '@billx/types';

import { userApiClient } from '../clients';
import type { NucleusServiceResponse } from './types';
import { handleAxiosError } from './utils';

export interface InvitationStats {
  total: number;
  pending: number;
  accepted: number;
  declined: number;
  expired: number;
}

export class InvitationService {
  /**
   * Create a new invitation
   */
  static async createInvitation(
    invitationData: CreateInvitationRequest
  ): Promise<NucleusServiceResponse<Invitation>> {
    try {
      const response = await userApiClient.post('/invitations', invitationData);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to create invitation');
    }
  }

  /**
   * Get invitations for the current user
   */
  static async fetchInvitationsForUser(
    filters: InvitationFilters
  ): Promise<NucleusServiceResponse<InvitationForUser[]>> {
    try {
      const params = new URLSearchParams();
      if (filters.status) {
        params.append('status', filters.status.join(','));
      }

      const response = await userApiClient.get('/user/invitations', { params });

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch invitations for user');
    }
  }

  /**
   * Get invitations for a business
   */
  static async fetchInvitationsByBusiness(
    businessId: string,
    filters: InvitationFilters
  ): Promise<NucleusServiceResponse<Invitation[]>> {
    try {
      const params = new URLSearchParams();
      params.append('businessId', businessId);
      if (filters.status) {
        params.append('status', filters.status.join(','));
      }
      const response = await userApiClient.get('/invitations', { params });

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch invitations by business');
    }
  }

  /**
   * Get invitation statistics for a business
   */
  static async getInvitationStats(
    businessId: string
  ): Promise<NucleusServiceResponse<InvitationStats>> {
    try {
      const params = new URLSearchParams();
      params.append('businessId', businessId);
      const response = await userApiClient.get('/invitations/stats', {
        params,
      });

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch invitation stats');
    }
  }

  /**
   * Get invitation by code for public
   */
  static async getInvitationByCodeForPublic(
    code: string
  ): Promise<NucleusServiceResponse<InvitationForPublic>> {
    try {
      const response = await userApiClient.get(
        `/public/invitations/code/${code}`
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch invitation stats');
    }
  }

  /**
   * Get invitation by ID
   */
  static async getInvitationById(
    id: string
  ): Promise<NucleusServiceResponse<Invitation>> {
    try {
      const response = await userApiClient.get(`/invitations/${id}`);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch invitation');
    }
  }

  /**
   * Update an invitation
   */
  static async updateInvitation(
    invitationId: string,
    updateData: UpdateInvitationRequest
  ): Promise<NucleusServiceResponse<Invitation>> {
    try {
      const response = await userApiClient.patch(
        `/invitations/${invitationId}`,
        updateData
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update invitation');
    }
  }

  /**
   * Accept an invitation
   */
  static async acceptInvitation(
    invitationId: string
  ): Promise<NucleusServiceResponse<Invitation>> {
    try {
      const response = await userApiClient.post(
        `/user/invitations/${invitationId}/accept`
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to accept invitation');
    }
  }

  /**
   * Decline an invitation
   */
  static async declineInvitation(
    invitationId: string,
    userId: string
  ): Promise<NucleusServiceResponse<Invitation>> {
    try {
      const response = await userApiClient.post(
        `/user/invitations/${invitationId}/decline`,
        { userId }
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to decline invitation');
    }
  }

  /**
   * Delete an invitation
   */
  static async deleteInvitation(
    invitationId: string
  ): Promise<NucleusServiceResponse<void>> {
    try {
      const response = await userApiClient.delete(
        `/invitations/${invitationId}`
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to delete invitation');
    }
  }
}
